import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconSearch, IconX } from '@tabler/icons-react';
import {
  Group,
  Loader,
  ProximityScope,
  ScrollArea,
  Text,
  useDebouncedValue,
  WP_ICON_NAV,
} from '@wapharma/design-system';
import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';
import { getToken } from '../auth';
import styles from './NavBarSearch.module.css';

type Federated = {
  products: Array<{ id: string; name: string; slug: string; status: string; reference?: string }>;
  orders: Array<{ id: string; reference: string | null; status: string }>;
  suppliers: Array<{ id: string; name: string; slug: string }>;
  b2bClients: Array<{ id: string; name: string; reference: string }>;
};

type MeiliHit = Record<string, unknown>;

function productSearchLabel(name: string, reference?: string | null): string {
  const ref = String(reference ?? '')
    .trim()
    .split(/\s+/)[0];
  if (!ref) return name;
  return `${name} · #${ref}`;
}

type Suggestion =
  | { kind: 'product'; id: string; label: string; badge: string; to: string }
  | { kind: 'order'; id: string; label: string; badge: string; to: string }
  | { kind: 'supplier'; id: string; label: string; badge: string; to: string }
  | { kind: 'b2b'; id: string; label: string; badge: string; to: string };

function buildSuggestions(fed: Federated | null, meili: MeiliHit[]): Suggestion[] {
  const out: Suggestion[] = [];
  const seenProducts = new Set<string>();

  for (const h of meili) {
    const id = typeof h.id === 'string' ? h.id : String(h.id ?? '');
    if (!id || seenProducts.has(id)) continue;
    seenProducts.add(id);
    const name = typeof h.name === 'string' ? h.name : 'Produit';
    const ref = typeof h.reference === 'string' ? h.reference : null;
    out.push({
      kind: 'product',
      id,
      label: productSearchLabel(name, ref),
      badge: 'Produit',
      to: `/catalog?product=${encodeURIComponent(id)}`,
    });
    if (out.length >= 6) break;
  }

  if (fed) {
    for (const p of fed.products) {
      if (seenProducts.has(p.id)) continue;
      seenProducts.add(p.id);
      out.push({
        kind: 'product',
        id: p.id,
        label: productSearchLabel(p.name, p.reference ?? p.slug),
        badge: 'Produit',
        to: `/catalog?product=${encodeURIComponent(p.id)}`,
      });
      if (out.filter((s) => s.kind === 'product').length >= 8) break;
    }
    for (const o of fed.orders.slice(0, 5)) {
      out.push({
        kind: 'order',
        id: o.id,
        label: o.reference ?? o.id.slice(0, 8),
        badge: 'Commande',
        to: `/orders/${encodeURIComponent(o.id)}`,
      });
    }
    for (const s of fed.suppliers.slice(0, 3)) {
      out.push({
        kind: 'supplier',
        id: s.id,
        label: s.name,
        badge: 'Fournisseur',
        to: '/procurement/suppliers',
      });
    }
    for (const c of fed.b2bClients.slice(0, 3)) {
      out.push({
        kind: 'b2b',
        id: c.id,
        label: `${c.name} (${c.reference})`,
        badge: 'Client pro',
        to: `/customer/pro/${c.id}`,
      });
    }
  }

  return out.slice(0, 16);
}

export function NavBarSearch() {
  const token = getToken();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q.trim(), 280);
  const [fed, setFed] = useState<Federated | null>(null);
  const [meiliHits, setMeiliHits] = useState<MeiliHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!token || !debounced) {
      setFed(null);
      setMeiliHits([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void Promise.all([
      apiGet<{ data: Federated } | Federated>(`/search?q=${encodeURIComponent(debounced)}`, token)
        .then((body) => unwrapData<Federated>(body))
        .catch(() => null),
      apiGet<{ data: MeiliHit[] }>(`/search/products?q=${encodeURIComponent(debounced)}`, token)
        .then((body) => {
          const d = unwrapData<MeiliHit[]>(body as { data: MeiliHit[] });
          return Array.isArray(d) ? d : [];
        })
        .catch(() => [] as MeiliHit[]),
    ])
      .then(([f, m]) => {
        if (cancelled) return;
        setFed(f);
        setMeiliHits(Array.isArray(m) ? m : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, token]);

  const suggestions = useMemo(() => buildSuggestions(fed, meiliHits), [fed, meiliHits]);

  const showPanel = menuOpen && debounced.length >= 1;

  const goSearchPage = useCallback(() => {
    const t = q.trim();
    if (!t) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(t)}`);
  }, [navigate, q]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setMenuOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions[0]) {
        navigate(suggestions[0].to);
        setMenuOpen(false);
        setQ('');
      } else {
        goSearchPage();
      }
    }
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={styles.searchRow}>
        <IconSearch className={styles.icon} {...WP_ICON_NAV} aria-hidden />
        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          placeholder="Rechercher…"
          aria-label="Rechercher dans l'admin"
          value={q}
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls="nav-search-suggestions"
          autoComplete="off"
          onChange={(e) => {
            setQ(e.target.value);
            setMenuOpen(true);
          }}
          onFocus={() => setMenuOpen(true)}
          onKeyDown={onKeyDown}
        />
        {q ? (
          <button
            type="button"
            className={styles.clear}
            aria-label="Effacer la recherche"
            title="Effacer"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setQ('');
              setMenuOpen(false);
              inputRef.current?.focus();
            }}
          >
            <IconX {...WP_ICON_NAV} stroke={1.5} aria-hidden />
          </button>
        ) : null}
      </div>

      {showPanel ? (
        <div id="nav-search-suggestions" className={styles.dropdown} role="listbox" aria-label="Suggestions">
          <ScrollArea className={styles.dropdownScroll} fade="y" maxHeight="min(320px, 48vh)">
            {loading ? (
              <div className={styles.loading}>
                <Group gap="sm" align="center">
                  <Loader />
                  <Text size="sm" c="dimmed">
                    Recherche…
                  </Text>
                </Group>
              </div>
            ) : suggestions.length === 0 ? (
              <div className={styles.empty}>Aucun résultat</div>
            ) : (
              <ProximityScope axis="y" className={styles.hitList}>
                {suggestions.map((s) => (
                  <Link
                    key={`${s.kind}-${s.id}-${s.to}`}
                    to={s.to}
                    className={styles.row}
                    role="option"
                    data-wp-proximity-item=""
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setMenuOpen(false);
                      setQ('');
                    }}
                  >
                    <span className={styles.badge}>{s.badge}</span>
                    <span className={styles.label}>{s.label}</span>
                  </Link>
                ))}
              </ProximityScope>
            )}
          </ScrollArea>
          <div className={styles.footer}>
            <Link to={`/search?q=${encodeURIComponent(debounced)}`} onMouseDown={(e) => e.preventDefault()}>
              Voir tout dans Recherche →
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
